import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_inbound_regional_policies'

const URLS = {
  CHARSET: 'http://a4esl.org/c/charset.html'
}

function InboundRegionalPoliciesI18n({
  classes,
  productName,
  hasCpl
}: {
  classes: ReturnType<typeof styles>
  productName?: string
  hasCpl?: boolean
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatValues = { productName }

  return (
    <div className={classes.content} data-testid="content">
      {formatMessage('note', formatValues)}
      <br />
      <br />
      {formatMessage('intro_prefix', formatValues)}
      {!hasCpl && <>{formatMessage('intro_quarantine')}</>}
      {formatMessage('intro_suffix')}
      <br />
      <br />
      <h3>{formatMessage('sender_policies.title')}</h3>
      {formatMessage('sender_policies.intro_prefix')}
      {!hasCpl && <>{formatMessage('sender_policies.intro_quarantine')}</>}
      {formatMessage('sender_policies.intro_suffix')}
      <ul>
        <li>
          <b>{formatMessage('sender_policies.country.title')}</b>
          <br />
          {formatMessage('sender_policies.country.description_prefix')}
          {!hasCpl && <>{formatMessage('sender_policies.country.description_quarantine')}</>}
          {formatMessage('sender_policies.country.description_suffix')}
        </li>
        <li>
          <b>{formatMessage('sender_policies.policy.title')}</b>
          <br />
          {formatMessage('sender_policies.policy.description_prefix')}
          {!hasCpl && <>{formatMessage('sender_policies.policy.description_quarantine')}</>}
          {formatMessage('sender_policies.policy.description_suffix')}
        </li>
        <li>
          <b>{formatMessage('sender_policies.comment.title')}</b>
          <br />
          {formatMessage('sender_policies.comment.description')}
        </li>
        <li>
          <b>{formatMessage('sender_policies.actions.title')}</b>
          <br />
          {formatMessage('sender_policies.actions.description')}
        </li>
        <li>
          <b>{formatMessage('sender_policies.geoip_exemptions_email.title')}</b>
          <br />
          {formatMessage('sender_policies.geoip_exemptions_email.description')}
        </li>
        <li>
          <b>{formatMessage('sender_policies.geoip_exemptions_ip.title')}</b>
          <br />
          {formatMessage('sender_policies.geoip_exemptions_ip.description')}
        </li>
      </ul>
      {!hasCpl && (
        <>
          <h3>{formatMessage('content_policies.title')}</h3>
          {formatMessage('content_policies.intro', formatValues)}
          <ul>
            <li>
              <b>{formatMessage('content_policies.language.title')}</b>
              <br />
              {formatMessage('content_policies.language.description')}
            </li>
            <li>
              <b>{formatMessage('content_policies.policy.title')}</b>
              <br />
              {formatMessage('content_policies.policy.description')}
            </li>
            <li>
              <b>{formatMessage('content_policies.comment.title')}</b>
              <br />
              {formatMessage('content_policies.comment.description')}
            </li>
            <li>
              <b>{formatMessage('content_policies.actions.title')}</b>
              <br />
              {formatMessage('content_policies.actions.description')}
            </li>
            <li>
              <b>{formatMessage('content_policies.exemptions.title')}</b>
              <br />
              {formatMessage('content_policies.exemptions.description')}
            </li>
          </ul>
          {formatMessage('content_policies.note_prefix')}
          <a href={URLS.CHARSET} target="_blank" rel="noreferrer">
            {formatMessage('content_policies.note_link')}
          </a>
          {formatMessage('content_policies.note_suffix')}
        </>
      )}
    </div>
  )
}

function InboundRegionalPolicies(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <InboundRegionalPoliciesI18n classes={classes} productName={props.productName} hasCpl={props.hasCpl} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <b>Note</b>: These settings apply to all domains you have verified in {props.productName} for processing email,
      unless you change these settings for a specific domain. In that case, the domain-level settings override the
      global system settings.
      <br />
      <br />
      Use the {props.productName} Regional Policies feature to <b>Block</b>{' '}
      {!props.hasCpl && (
        <>
          or <b>Quarantine</b>
        </>
      )}{' '}
      inbound messages based on the country of origin or character set.
      <br />
      <br />
      <h3>Sender Policies</h3>
      Use the <b>GeoIP Policies</b> section to <b>Block</b>{' '}
      {!props.hasCpl && (
        <>
          or <b>Quarantine</b>
        </>
      )}{' '}
      by country of origin:
      <ul>
        <li>
          <b>Country</b>
          <br />
          Select the country you want to <b>Block</b>{' '}
          {!props.hasCpl && (
            <>
              or <b>Quarantine</b>
            </>
          )}{' '}
          from the drop-down menu.
        </li>
        <li>
          <b>Policy</b>
          <br />
          Select <b>Block</b>{' '}
          {!props.hasCpl && (
            <>
              or <b>Quarantine</b>
            </>
          )}{' '}
          from the drop-down menu for the selected country.
        </li>
        <li>
          <b>Comment</b>
          <br />
          You can add an optional comment about the selected country.
        </li>
        <li>
          <b>Actions</b>
          <br />
          Click <b>Add</b> to add the selected country, or click <b>Remove</b> to remove an entry from the list.
        </li>
        <li>
          <b>GeoIP Exemptions by Email Address/Domains</b>
          <br />
          Enter the <b>Email Address/Domain</b> and optional <b>Comment</b>. To add, edit, or remove multiple exemptions
          at once, use the <b>Bulk Edit</b> button.
        </li>
        <li>
          <b>GeoIP Exemptions by Sender IP Address</b>
          <br />
          Enter the sender <b>IP Address</b>, <b>Netmask</b>, and optional <b>Comment</b>. To add, edit, or remove
          multiple exemptions at once, use the <b>Bulk Edit</b> button.
        </li>
      </ul>
      {!props.hasCpl && (
        <>
          <h3>Content Policies</h3>
          Use the <b> Language Policies</b> section to <b> Block</b> or <b> Quarantine</b> by character class.
          {props.productName}
          uses a natural language detection library to determine the language in the mail.
          <ul>
            <li>
              <b>Language</b>
              <br />
              Select the language you want to <b>Block</b> or <b>Quarantine</b> from the drop-down menu.
            </li>
            <li>
              <b>Policy</b>
              <br />
              Select <b>Block</b> or <b>Quarantine</b> from the drop-down menu for the selected language.
            </li>
            <li>
              <b>Comment</b>
              <br />
              You can add an optional comment about the selected language.
            </li>
            <li>
              <b>Actions</b>
              <br />
              Click <b>Add</b> to add the selected language, or click <b>Remove</b> to remove an entry from the list.
            </li>
            <li>
              <b>Language Policy Exemptions</b>
              <br />
              Enter the sender <b>Email Address/Domain</b> and optional <b>Comment</b>. To add, edit, or remove multiple
              exemptions at once, use the <b>Bulk Edit</b> button.
            </li>
          </ul>
          <b>Note: </b>To block or quarantine mail based on a specific language character set, go to the{' '}
          <b> Content Policies</b> section. See{' '}
          <a href="http://a4esl.org/c/charset.html" target="_blank" rel="noreferrer">
            {' '}
            http://a4esl.org/c/charset.html
          </a>{' '}
          for a list of widely used language character sets.
        </>
      )}
    </div>
  )
}

export default InboundRegionalPolicies
